import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './Payments.css';

const EMPTY = {
  payment_date: today(),
  supplier_id: '',
  party_name: '',
  payment_type: '',
  bill_no: '',
  bill_amount: '',
  paid_amount: '',
  mode: 'Cash',
  reference_no: '',
  remarks: '',
};

// Payment heads. Salary posts to Expense (shows in P&L / accounting reports);
// Employee Advance posts to the Advances asset (recoverable loan, not an expense).
const PAYMENT_TYPES = [
  { value: '',        label: 'Expense / Supplier Payment' },
  { value: 'salary',  label: 'Employee Salary' },
  { value: 'advance', label: 'Employee Advance' },
];
const payTypeLabel = (v) => (PAYMENT_TYPES.find(t => t.value === (v || ''))?.label) || '—';

export default function Payments() {
  const [date, setDate] = useState(today());
  const { data: payments, loading, error, reload } = useApi(
    () => api.payments.list(date), [date]
  );
  const { data: suppliers } = useApi(() => api.suppliers.list(), [], []);
  const { data: banksData } = useApi(() => api.banks.list(), [], { banks: [] });
  const { data: dReceipts } = useApi(() => api.dailyReceipts.list(''), []);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const onSupplierChange = (e) => {
    const sid = e.target.value;
    const sup = (suppliers || []).find(s => String(s.id) === sid);
    setForm(f => ({
      ...f,
      supplier_id: sid,
      party_name: sup ? sup.name : f.party_name,
    }));
  };

  const handleSave = async () => {
    if (!form.party_name.trim()) { notify('❌ Party name required'); return; }
    const paid = parseFloat(form.paid_amount || 0);
    if (paid <= 0) { notify('❌ Paid amount must be greater than 0'); return; }
    setSaving(true);
    try {
      await api.payments.create({
        ...form,
        supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null,
        paid_amount: paid,
        bill_amount: parseFloat(form.bill_amount || 0),
      });
      notify('✅ Payment recorded');
      setShowForm(false);
      setForm(EMPTY);
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.payments.remove(confirm.id);
      notify('✅ Payment deleted (ledger reversed)');
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setConfirm(null);
  };

  const total = (payments || []).reduce((s, p) => s + Number(p.paid_amount || 0), 0);
  const banks = banksData.banks || [];

  return (
    <div className="payments-page">
      <div className="page-header">
        <h2 className="page-title">Payments (Money Out)</h2>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => { setShowForm(!showForm); setForm(EMPTY); }}
        >
          {showForm ? '✕ Cancel' : '＋ New Payment'}
        </button>
      </div>

      {msg && <div className="pay-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {showForm && (
        <div className="section-card">
          <div className="section-header">Record New Payment</div>
          <div className="pay-form-grid">
            <div className="form-group">
              <label>Date *</label>
              <input type="date" value={form.payment_date}
                     onChange={e => setForm({ ...form, payment_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Supplier (optional)</label>
              <select value={form.supplier_id} onChange={onSupplierChange}>
                <option value="">-- Other party --</option>
                {(suppliers || []).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Payment Type</label>
              <select value={form.payment_type}
                      onChange={e => setForm({ ...form, payment_type: e.target.value })}>
                {PAYMENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Party Name *</label>
              <input value={form.party_name}
                     onChange={e => setForm({ ...form, party_name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Bill No (from Daily Receipts)</label>
              <select value={form.bill_no} onChange={e => {
                const val = e.target.value;
                const match = (dReceipts || []).find(r => r.receipt_no === val);
                setForm({ ...form, bill_no: val, bill_amount: match ? match.amount : form.bill_amount });
              }}>
                <option value="">-- Select Bill --</option>
                {Array.from(new Set((dReceipts || []).map(r => r.receipt_no))).filter(Boolean).map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Bill Amount</label>
              <input type="number" step="0.01" value={form.bill_amount}
                     onChange={e => setForm({ ...form, bill_amount: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Paid Amount *</label>
              <input type="number" step="0.01" value={form.paid_amount}
                     onChange={e => setForm({ ...form, paid_amount: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Mode *</label>
              <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                <option value="Cash">Cash</option>
                {banks.map(b => (
                  <option key={b.id} value={`Bank:${b.id}`}>
                    {b.name}{Number(b.is_default) === 1 ? ' (default)' : ''}
                  </option>
                ))}
                <option value="UPI">UPI (default bank)</option>
                <option value="NEFT">NEFT (default bank)</option>
                <option value="RTGS">RTGS (default bank)</option>
                <option value="Cheque">Cheque (default bank)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Reference No (Cheque/UTR/UPI)</label>
              <input value={form.reference_no}
                     onChange={e => setForm({ ...form, reference_no: e.target.value })} />
            </div>
            <div className="form-group form-group-wide">
              <label>Remarks</label>
              <input value={form.remarks}
                     onChange={e => setForm({ ...form, remarks: e.target.value })} />
            </div>
          </div>
          <div style={{ padding: '0 12px 12px' }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? '⏳ Saving...' : 'Save Payment'}
            </button>
          </div>
        </div>
      )}

      <div className="pay-filters">
        <div className="form-group">
          <label>Filter by date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
          <strong>Total paid on {fmtDate(date)}: </strong>
          <span style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--danger)' }}>
            ₹ {fmtNum(total)}
          </span>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">Payments on {fmtDate(date)}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Payment No</th>
                <th style={{ textAlign: 'left' }}>Party</th>
                <th style={{ textAlign: 'left' }}>Type</th>
                <th style={{ textAlign: 'left' }}>Bill No</th>
                <th style={{ textAlign: 'left' }}>Mode</th>
                <th style={{ textAlign: 'left' }}>Ref</th>
                <th style={{ textAlign: 'right' }}>Paid</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(payments || []).length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  No payments on this date
                </td></tr>
              ) : (payments || []).map(p => (
                <tr key={p.id}>
                  <td style={{ textAlign: 'left', fontWeight: 600, fontFamily: 'monospace' }}>{p.payment_no}</td>
                  <td style={{ textAlign: 'left' }}>{p.party_name}</td>
                  <td style={{ textAlign: 'left', fontSize: 12, color: p.payment_type ? 'var(--text)' : 'var(--text-muted)' }}>
                    {p.payment_type ? payTypeLabel(p.payment_type) : '—'}
                  </td>
                  <td style={{ textAlign: 'left' }}>{p.bill_no || '—'}</td>
                  <td style={{ textAlign: 'left' }}>{p.mode}</td>
                  <td style={{ textAlign: 'left', fontSize: 11.5, color: 'var(--text-muted)' }}>{p.reference_no || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    ₹ {fmtNum(p.paid_amount)}
                  </td>
                  <td>
                    <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                            onClick={() => setConfirm(p)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Delete Payment?"
        message={confirm ? `Delete payment ${confirm.payment_no}? The ledger entry will be reversed — supplier balance and bank balance will revert.` : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel="Delete"
      />
    </div>
  );
}
